import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Syringe, Layers, Heart, Ear, ArrowRight, Activity, Scan } from 'lucide-react';
import { categories, procedures } from '@/data/procedures';

const categoryIcons: Record<string, React.ReactNode> = {
  msk: <Syringe className="w-6 h-6" />,
  derm: <Layers className="w-6 h-6" />,
  womens: <Heart className="w-6 h-6" />,
  audiology: <Ear className="w-6 h-6" />,
  pocus: <Scan className="w-6 h-6" />,
};

const categoryColors: Record<string, string> = {
  msk: 'bg-blue-50 text-blue-700 border-blue-200',
  derm: 'bg-amber-50 text-amber-700 border-amber-200',
  womens: 'bg-rose-50 text-rose-700 border-rose-200',
  audiology: 'bg-violet-50 text-violet-700 border-violet-200',
  pocus: 'bg-teal-50 text-teal-700 border-teal-200',
};

const categoryHeaderColors: Record<string, string> = {
  msk: 'text-blue-700',
  derm: 'text-amber-700',
  womens: 'text-rose-700',
  audiology: 'text-violet-700',
  pocus: 'text-teal-700',
};

export default function HomePage() {
  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07 } },
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-10"
      >
        <div className="flex items-center gap-2 mb-3">
          <Activity className="w-5 h-5 text-primary" />
          <span className="text-sm font-semibold text-primary uppercase tracking-widest">Primary Health Care · Des Moines, IA</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-foreground leading-tight mb-3">
          FM Procedure Hub
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg max-w-2xl leading-relaxed">
          Quick-access procedural reference for family medicine residents. Designed for point-of-care use during clinic. Use the search bar or browse by category below.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-primary/8 text-primary border border-primary/20 px-3 py-1.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />
            22 Procedures
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted text-muted-foreground border border-border px-3 py-1.5 rounded-full">
            Point-of-Care Optimized
          </span>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-muted text-muted-foreground border border-border px-3 py-1.5 rounded-full">
            Mobile Friendly
          </span>
        </div>
      </motion.div>

      {/* Disclaimer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
        className="mb-8 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800"
      >
        <strong>Educational Resource Only.</strong> This hub is intended as a procedural reference for residents and does not replace clinical judgment, attending supervision, or institutional protocols. Always verify contraindications for individual patients.
      </motion.div>

      {/* Categories Grid */}
      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        className="space-y-8"
      >
        {categories.map((cat) => {
          const procs = procedures.filter((p) => p.categoryId === cat.id);
          return (
            <motion.div key={cat.id} variants={fadeUp}>
              {/* Category header */}
              <div className="flex items-center gap-2.5 mb-3">
                <span className={`p-1.5 rounded-md border ${categoryColors[cat.id]}`}>
                  {categoryIcons[cat.id]}
                </span>
                <h2 className={`text-lg font-semibold ${categoryHeaderColors[cat.id]}`}>
                  {cat.label}
                </h2>
                <span className="text-xs text-muted-foreground ml-1">
                  {procs.length} procedure{procs.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Procedure cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {procs.map((proc) => (
                  <Link
                    key={proc.id}
                    to={`/procedure/${proc.id}`}
                    className="group flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:border-primary/40 hover:shadow-sm transition-all duration-200"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground group-hover:text-primary transition-colors leading-snug">
                        {proc.title}
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 truncate">
                        {proc.overview.indications[0]}
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all flex-shrink-0 ml-2" />
                  </Link>
                ))}
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Bottom info */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.4 }}
        className="mt-12 pt-6 border-t border-border text-center"
      >
        <p className="text-xs text-muted-foreground">
          Primary Health Care · 250 Laurel St, Des Moines, IA 50314 · 515-612-9595
          <br />
          For questions or updates, contact your program director.
        </p>
      </motion.div>
    </div>
  );
}
